import { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import * as api from '../utils/api';
import { useToast } from './ToastContext';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const { showToast } = useToast();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [appliances, setAppliances] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [home, setHome] = useState(null);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadedRef = useRef({});
  const loadingCount = useRef(0);

  const startLoading = () => {
    loadingCount.current++;
    setLoading(true);
  };
  const stopLoading = () => {
    loadingCount.current--;
    if (loadingCount.current === 0) setLoading(false);
  };

  const fetchDashboard = useCallback(async () => {
    startLoading();
    try {
      const { data } = await api.getDashboard();
      setTasks(data.tasks);
      setProjects(data.projects);
      setExpenses(data.expenses);
      setAppliances(data.appliances);
      setLowStockCount(data.lowStockCount);
      loadedRef.current.tasks = true;
      loadedRef.current.projects = true;
      loadedRef.current.expenses = true;
      loadedRef.current.appliances = true;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load dashboard';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      stopLoading();
    }
  }, [showToast]);

  const fetchTasks = useCallback(async ({ force = false } = {}) => {
    if (!force && loadedRef.current.tasks) return;
    startLoading();
    try {
      const { data } = await api.getTasks();
      setTasks(data);
      loadedRef.current.tasks = true;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load tasks';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      stopLoading();
    }
  }, [showToast]);

  const fetchProjects = useCallback(async ({ force = false } = {}) => {
    if (!force && loadedRef.current.projects) return;
    startLoading();
    try {
      const { data } = await api.getProjects();
      setProjects(data);
      loadedRef.current.projects = true;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load projects';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      stopLoading();
    }
  }, [showToast]);

  const patchProjectMaterial = useCallback((projectId, materialIndex, patch) => {
    setProjects((prev) => prev.map((p) =>
      p._id === projectId
        ? { ...p, materials: p.materials.map((m, i) => i === materialIndex ? { ...m, ...patch } : m) }
        : p
    ));
  }, []);

  const patchProjectMaterials = useCallback((projectId, newMaterials) => {
    setProjects((prev) => prev.map((p) =>
      p._id === projectId ? { ...p, materials: newMaterials } : p
    ));
  }, []);

  const markTaskComplete = useCallback(async (id, body = {}) => {
    try {
      const { data } = await api.completeTask(id, body);
      setTasks((prev) => prev.map((t) => (t._id === id ? data.task : t)));
      showToast('Task completed', 'success');
      return data.linkedConsumables || [];
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to complete task', 'error');
      throw err;
    }
  }, [showToast]);

  const removeTask = useCallback(async (id) => {
    try {
      await api.deleteTask(id);
      setTasks((prev) => prev.filter((t) => t._id !== id));
      showToast('Task deleted', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete task', 'error');
      throw err;
    }
  }, [showToast]);

  const removeProject = useCallback(async (id) => {
    try {
      await api.deleteProject(id);
      setProjects((prev) => prev.filter((p) => p._id !== id));
      showToast('Project deleted', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete project', 'error');
      throw err;
    }
  }, [showToast]);

  const fetchRooms = useCallback(async ({ force = false } = {}) => {
    if (!force && loadedRef.current.rooms) return;
    startLoading();
    try {
      const { data } = await api.getRooms();
      setRooms(data);
      loadedRef.current.rooms = true;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load rooms';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      stopLoading();
    }
  }, [showToast]);

  const removeRoom = useCallback(async (id) => {
    try {
      await api.deleteRoom(id);
      setRooms((prev) => prev.filter((r) => r._id !== id));
      showToast('Room deleted', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete room', 'error');
      throw err;
    }
  }, [showToast]);

  const fetchAppliances = useCallback(async ({ force = false } = {}) => {
    if (!force && loadedRef.current.appliances) return;
    startLoading();
    try {
      const { data } = await api.getAppliances();
      setAppliances(data);
      loadedRef.current.appliances = true;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load appliances';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      stopLoading();
    }
  }, [showToast]);

  const removeAppliance = useCallback(async (id) => {
    try {
      await api.deleteAppliance(id);
      setAppliances((prev) => prev.filter((a) => a._id !== id));
      showToast('Appliance deleted', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete appliance', 'error');
      throw err;
    }
  }, [showToast]);

  const fetchContacts = useCallback(async ({ force = false } = {}) => {
    if (!force && loadedRef.current.contacts) return;
    startLoading();
    try {
      const { data } = await api.getContacts();
      setContacts(data);
      loadedRef.current.contacts = true;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load contacts';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      stopLoading();
    }
  }, [showToast]);

  const removeContact = useCallback(async (id) => {
    try {
      await api.deleteContact(id);
      setContacts((prev) => prev.filter((c) => c._id !== id));
      showToast('Contact deleted', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete contact', 'error');
      throw err;
    }
  }, [showToast]);

  const fetchExpenses = useCallback(async ({ force = false } = {}) => {
    if (!force && loadedRef.current.expenses) return;
    startLoading();
    try {
      const { data } = await api.getExpenses();
      setExpenses(data);
      loadedRef.current.expenses = true;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load expenses';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      stopLoading();
    }
  }, [showToast]);

  const removeExpense = useCallback(async (id) => {
    try {
      await api.deleteExpense(id);
      setExpenses((prev) => prev.filter((e) => e._id !== id));
      showToast('Expense deleted', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete expense', 'error');
      throw err;
    }
  }, [showToast]);

  const fetchHome = useCallback(async () => {
    try {
      const { data } = await api.getHome();
      setHome(data);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load home profile';
      setError(msg);
      showToast(msg, 'error');
    }
  }, [showToast]);

  const saveHome = useCallback(async (data) => {
    try {
      const { data: updated } = await api.updateHome(data);
      setHome(updated);
      showToast('Home profile saved', 'success');
      return updated;
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save home profile', 'error');
      throw err;
    }
  }, [showToast]);

  // Memoized so consumers only re-render when state actually changes (the
  // callbacks are all useCallback-stable)
  const value = useMemo(() => ({
    tasks, projects, rooms, appliances, contacts, expenses, home, lowStockCount, loading, error,
    fetchDashboard, fetchTasks, fetchProjects, fetchRooms, fetchAppliances, fetchContacts, fetchExpenses, fetchHome,
    markTaskComplete, removeTask, removeProject, removeRoom, removeAppliance, removeContact, removeExpense,
    patchProjectMaterial, patchProjectMaterials, saveHome, setError,
  }), [
    tasks, projects, rooms, appliances, contacts, expenses, home, lowStockCount, loading, error,
    fetchDashboard, fetchTasks, fetchProjects, fetchRooms, fetchAppliances, fetchContacts, fetchExpenses, fetchHome,
    markTaskComplete, removeTask, removeProject, removeRoom, removeAppliance, removeContact, removeExpense,
    patchProjectMaterial, patchProjectMaterials, saveHome,
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
